import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const tips = [
  {
    id: "electrical-systems",
    icon: "ri-plug-2-line",
    title: "Check Electrical Systems Regularly",
    content:
      "Electrical fires are among the most common sources of fires in business premises. Electrical systems include computers, printers, telephones, power points and other appliances. To reduce chances of fire resulting from electrical faults, make sure that all the electrical equipments are regularly checked. Employees should be educated on how to perform simple checks on the machines that they use regularly to ensure any faults are detected and resolved on time.",
  },
  {
    id: "remove-hazards",
    icon: "ri-delete-bin-line",
    title: "Remove Any Fire Hazards from the Workplace",
    content:
      "There are lots of other fire hazards in workplaces that your employees may not know about. The most common ones include unprotected combustible materials, damaged electrical appliances and obstacles at the fire exits. If these hazards are left lying around in the workplace, they could easily start fires, putting people and assets at risk. In case there are any damaged equipment or machines in the workplace, they should be removed or replaced to eliminate fire hazards. All the machines in the workplace should also remain dry, clean and well ventilated at all times. Any hazardous chemicals or substances in the workplace should be kept in protected areas after use.",
  },
  {
    id: "mandatory-training",
    icon: "ri-graduation-cap-line",
    title: "Mandatory Fire Safety Training",
    content:
      "Offering mandatory fire safety training for all your employees will also go a long way in improving fire protection. The training should focus on how employees can effectively detect fire hazards and the actions to take during fire emergencies. Workers should be aware of all the exit routes on the building and how to safely access them during fires. Fire safety training should also take in consideration the needs of disabled persons among the staff. With proper fire safety training and fire protection systems, there are many situations that employees can effectively resolve without waiting for fire rescue teams. Employees should also undergo fire safety drills after the training to put the acquired skills to test.",
  },
  {
    id: "fire-warden",
    icon: "ri-user-star-line",
    title: "Designate a Fire Warden",
    content:
      "Even after training all the workers on fire safety, it is still important that your business has one designated fire warden to take charge in case of a fire incident. Fire wardens perform a wide range of duties during fire emergencies to ensure safe evacuation of occupants and appropriate response to suppress the fire as soon as possible. A trained fire warden should be responsible for maintaining the evacuation plan, conducting fire risk assessment and ensuring that all fire protection systems are in proper working conditions.",
  },
  {
    id: "no-smoking",
    icon: "ri-forbid-2-line",
    title: "Initiate a No-Smoking Order in the Workplace",
    content:
      "Smoking in the workplace is also a major cause of fires in commercial and industrial spaces. While you cannot prevent people from smoking, there should be a designated space outside the workplace where people can smoke during breaks. All workplaces should be no-smoking zones and the notices should be placed at all departments for all employees to see.",
  },
  {
    id: "fire-alarms",
    icon: "ri-alarm-warning-line",
    title: "Be on the Alert for Fire and Smoke Alarms",
    content:
      "Some workplaces can be too crowded and busy that workers may easily ignore smoke and fire alarms. As such, it is important to stress to your workers the value of smoke and fire alarms on the premises. The employees are in the first line of defence against fires and should always be on high alert to facilitate prompt response during an emergency.",
  },
];

export default function FireSafetyTipsWorkplacePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: tips.map((tip) => ({
              "@type": "Question",
              name: tip.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: tip.content,
              },
            })),
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/82e249e9-dcb3-43cb-b23f-468dd970ac31_compressed_dreamstime_m_19769264.webp"
              alt="Fire safety escape hood emergency equipment"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Fire Safety Tips in the Workplace</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Workplace Safety
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Fire Safety Tips in the <span className="text-primary-400">Workplace for Employees</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Fire safety is something that all businesses should take very seriously — not just because of legal requirements, but to ensure employees and other people on the premises are protected against the impacts of fire emergencies.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Why It Matters
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why Fire Safety Training is Essential for All Employees
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Having the right protection systems on the building also facilitates quick and effective response to fires, thereby also saving your valuable assets from much destruction. Apart from just installing fire protection systems, businesses should also provide fire safety training for employees.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                The training should be conducted by Qualified Persons that understand the relevant fire safety regulations. The following are basic fire safety tips in the workplace that employees should keep in mind in order to avoid accidents and minimize destruction to property during fires.
              </p>
            </div>
            <div className="bg-primary-500 rounded-2xl p-6 text-background-50 flex flex-col justify-between">
              <div>
                <i className="ri-user-star-line text-3xl mb-3 block"></i>
                <h3 className="font-heading text-lg font-bold mb-2">QP Consultancy Services</h3>
                <p className="text-background-100 text-sm leading-relaxed mb-4">
                  Ezzogenics offers QP consultancy services that include professional fire safety training in the workplace for employees.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap mt-4"
              >
                Enquire Now
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Action Items
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Key Fire Safety Tips <span className="text-primary-500">at the Workplace</span>
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              {tips.map((tip, i) => (
                <div key={tip.id} id={tip.id} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex items-start gap-5">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${tip.icon} text-primary-600 text-xl`}></i>
                      </div>
                      <span className="text-xs font-bold text-foreground-400">0{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${tip.id}`}>{tip.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed">{tip.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Get Professional Fire Safety Training for Your Team
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            Ezzogenics offers QP consultancy services that also include professional fire safety training in the workplace for employees. Ensure your team is ready to protect lives and assets during fire emergencies.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Request Training
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              View Our Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}